import { ChangeEventHandler, KeyboardEventHandler, ReactNode, useCallback, useState } from 'react'

import { Command } from '@cowprotocol/types'
import { ButtonError, UI } from '@cowprotocol/ui'

import { Trans } from '@lingui/react/macro'
import styled from 'styled-components/macro'

const Container = styled.div``
const Instruction = styled.p`
  margin: 0;
  margin-bottom: 10px;
`
const Input = styled.input`
  border: 1px solid var(${UI.COLOR_BORDER});
  background: var(${UI.COLOR_PAPER_DARKER});
  color: inherit;
  width: 100%;
  margin: 10px 0;
  margin-top: 0;
  padding: 10px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: bold;

  &:focus {
    border: 1px solid var(${UI.COLOR_PRIMARY});
  }
`
interface ConfirmedButtonProps {
  className?: string
  onConfirm: Command
  children?: ReactNode
  action: string
  confirmWord: string
  skipInput?: boolean
  bottomContent?: ReactNode
}

export function ConfirmedButton({
  className,
  onConfirm,
  children,
  action,
  confirmWord,
  bottomContent,
  skipInput = false,
}: ConfirmedButtonProps): ReactNode {
  const [inputValue, setInputValue] = useState('')
  const onInputChange: ChangeEventHandler<HTMLInputElement> = (event) => setInputValue(event.target.value ?? '')
  const shouldShowInput = !skipInput
  const shouldButtonBeDisabled = shouldShowInput && !isValidConfirm(inputValue, confirmWord)
  const onKeyDown: KeyboardEventHandler = useCallback(
    (event) => {
      if (event.key.toLowerCase() !== 'enter' || shouldButtonBeDisabled) {
        return
      }

      onConfirm()
    },
    [onConfirm, shouldButtonBeDisabled],
  )

  return (
    <Container className={className} onKeyDown={onKeyDown}>
      {shouldShowInput ? (
        <Instruction>
          <Trans>
            Please type the word <strong>"{confirmWord}"</strong> to {action}.
          </Trans>
        </Instruction>
      ) : typeof bottomContent !== undefined ? (
        bottomContent
      ) : (
        <Instruction>
          <Trans>Please click confirm to {action}.</Trans>
        </Instruction>
      )}
      {shouldShowInput && <Input id="confirm-modal-input" onChange={onInputChange} />}
      <ButtonError
        id="confirm-modal-button"
        error
        padding={'12px'}
        disabled={shouldButtonBeDisabled}
        onClick={onConfirm}
      >
        {children}
      </ButtonError>
    </Container>
  )
}

function isValidConfirm(value: string, confirmWord: string): boolean {
  return typeof value === 'string' && value.toLowerCase().trim() === confirmWord
}
